import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { functions, httpsCallable } from './firebase.js';
import { branding, isDemoBuild, SALES_EMAIL } from './siteConfig.js';
import {
  FEATURE_CATEGORIES,
  ADDON_MODULES,
  PRICING_TIERS,
  MARKET_COMPARISON,
  MANAGEMENT_COMPARISON,
  AI_MANAGEMENT_PILLARS,
  SMART_HOME_INTEGRATIONS,
  WIZARD_QUESTIONS,
  recommendTier,
  suggestAddons,
  estimatePricing,
  buildRequirementsBrief,
} from './hoaProductCatalog.js';
import PmcReplacementChecklist from './PmcReplacementChecklist.js';

const INITIAL_ANSWERS = {
  communityName: '',
  contactEmail: '',
  lotCount: '',
  communityType: '',
  managementModel: '',
  priorities: [],
  integrations: [],
  timeline: '',
  branding: '',
  notes: '',
};

function HoaSalesPage({ isMobile, onTryDemo, onOwnerLogin }) {
  const [answers, setAnswers] = useState(INITIAL_ANSWERS);
  const [enabledAddons, setEnabledAddons] = useState(new Set());
  const [copyStatus, setCopyStatus] = useState('');
  const [submitStatus, setSubmitStatus] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);

  const { tier, lots } = useMemo(() => recommendTier(answers), [answers]);
  const suggestedAddons = useMemo(() => suggestAddons(answers), [answers]);

  useEffect(() => {
    setEnabledAddons((prev) => {
      const next = new Set(prev);
      suggestedAddons.forEach((a) => next.add(a.id));
      return next;
    });
  }, [suggestedAddons]);

  const activeAddons = useMemo(
    () => ADDON_MODULES.filter((a) => enabledAddons.has(a.id)),
    [enabledAddons]
  );
  const pricing = useMemo(() => estimatePricing(tier, lots, activeAddons), [tier, lots, activeAddons]);
  const brief = useMemo(
    () => buildRequirementsBrief(answers, tier, lots, activeAddons, pricing),
    [answers, tier, lots, activeAddons, pricing]
  );

  const wizardSections = [
    { title: 'Your community', ids: ['communityName', 'contactEmail', 'lotCount', 'communityType', 'managementModel'] },
    { title: 'Needs & integrations', ids: ['priorities', 'integrations'] },
    { title: 'Launch details', ids: ['timeline', 'branding', 'notes'] },
  ];

  const updateAnswer = useCallback((id, value) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
  }, []);

  const toggleMulti = useCallback((id, value, max) => {
    setAnswers((prev) => {
      const current = prev[id] || [];
      if (current.includes(value)) {
        return { ...prev, [id]: current.filter((v) => v !== value) };
      }
      if (max && current.length >= max) return prev;
      return { ...prev, [id]: [...current, value] };
    });
  }, []);

  const toggleAddon = useCallback((addonId) => {
    setEnabledAddons((prev) => {
      const next = new Set(prev);
      if (next.has(addonId)) next.delete(addonId);
      else next.add(addonId);
      return next;
    });
  }, []);

  const handleCopyBrief = async () => {
    try {
      await navigator.clipboard.writeText(brief);
      setCopyStatus('Copied to clipboard');
      setTimeout(() => setCopyStatus(''), 2500);
    } catch {
      setCopyStatus('Copy failed — select text manually');
    }
  };

  const handleSubmitBrief = async () => {
    const communityName = answers.communityName?.trim();
    const contactEmail = answers.contactEmail?.trim();
    if (!communityName || !contactEmail) {
      setSubmitStatus('Add your community name and contact email in step 1 first.');
      return;
    }
    if (!answers.lotCount) {
      setSubmitStatus('Complete the configurator (lot count) before submitting.');
      return;
    }

    setSubmitLoading(true);
    setSubmitStatus('');
    try {
      const submitLead = httpsCallable(functions, 'submitHoaLead');
      await submitLead({
        message: brief,
        contactEmail,
        communityName,
        tier: tier.id,
        lotCount: lots,
        estimatedMonthly: pricing.monthly,
        estimatedSetup: pricing.setup,
        addons: activeAddons.map((a) => a.id),
      });
      setSubmitStatus('Brief submitted — we will follow up by email.');
    } catch (error) {
      console.error('submitHoaLead failed:', error);
      setSubmitStatus(error.message || 'Submission failed. Try Email us or copy the brief.');
    } finally {
      setSubmitLoading(false);
    }
  };

  const mailtoSubject = encodeURIComponent(
    `Portal requirements — ${answers.communityName || 'Our HOA'}`
  );
  const mailtoBody = encodeURIComponent(brief);
  const salesEmail = isDemoBuild ? branding.contactEmail : SALES_EMAIL;

  const renderQuestion = (q) => {
    const value = answers[q.id];

    if (q.type === 'text' || q.type === 'email') {
      return (
        <label key={q.id} className="sales-field">
          <span className="sales-field-label">{q.label}{q.required ? ' *' : ''}</span>
          <input
            type={q.type}
            value={value}
            placeholder={q.placeholder}
            onChange={(e) => updateAnswer(q.id, e.target.value)}
            className="sales-input"
          />
        </label>
      );
    }

    if (q.type === 'textarea') {
      return (
        <label key={q.id} className="sales-field">
          <span className="sales-field-label">{q.label}</span>
          <textarea
            value={value}
            placeholder={q.placeholder}
            onChange={(e) => updateAnswer(q.id, e.target.value)}
            className="sales-textarea"
            rows={3}
          />
        </label>
      );
    }

    if (q.type === 'select') {
      return (
        <label key={q.id} className="sales-field">
          <span className="sales-field-label">{q.label}{q.required ? ' *' : ''}</span>
          <select
            value={value}
            onChange={(e) => updateAnswer(q.id, e.target.value)}
            className="sales-select"
          >
            <option value="">Select…</option>
            {q.options.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </label>
      );
    }

    if (q.type === 'multiselect') {
      return (
        <fieldset key={q.id} className="sales-field sales-fieldset">
          <legend className="sales-field-label">
            {q.label}{q.max ? ` (up to ${q.max})` : ''}
          </legend>
          <div className="sales-chip-grid">
            {q.options.map((opt) => {
              const checked = (value || []).includes(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  className={`sales-chip ${checked ? 'sales-chip-active' : ''}`}
                  onClick={() => toggleMulti(q.id, opt.value, q.max)}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </fieldset>
      );
    }

    return null;
  };

  const currentSection = wizardSections[wizardStep];
  const sectionQuestions = WIZARD_QUESTIONS.filter((q) => currentSection.ids.includes(q.id));

  return (
    <div className={`marketing-page sales-page ${isMobile ? 'marketing-page-mobile' : ''}`}>
      <section className="marketing-hero sales-hero">
        <div className="marketing-hero-glow" aria-hidden="true" />
        <div className="marketing-hero-inner">
          <p className="marketing-eyebrow">AI HOA management · skip the management company markup</p>
          <h1 className="marketing-headline">Self-manage your HOA with AI — not a PMC contract</h1>
          <p className="marketing-lead">
            Boards keep control. Nanny AI handles routine owner questions 24/7. Billing, gate access,
            violations, polls, and admin ops ship in one white-labeled portal — so you don&apos;t need a
            full-service management company for day-to-day work. Everything in{' '}
            {isDemoBuild ? 'Pine Ridge Estates' : 'Nantahala Mountain Resort'} is available for your community.
          </p>
          <div className="marketing-cta-row">
            {onTryDemo && (
              <button type="button" className="marketing-btn marketing-btn-primary" onClick={onTryDemo}>
                Try live demo
              </button>
            )}
            <a href="#smart-integrations" className="marketing-btn marketing-btn-secondary">
              Smart-home add-ons
            </a>
            <a href="#configurator" className="marketing-btn marketing-btn-secondary">
              Start configurator
            </a>
            <Link to="/for-communities/one-pager" className="marketing-btn marketing-btn-secondary">
              Board one-pager (PDF)
            </Link>
            <Link to="/" className="marketing-btn marketing-btn-ghost">
              Back to overview
            </Link>
          </div>
        </div>
      </section>

      <section className="marketing-stats">
        <article className="marketing-stat">
          <strong>Nanny AI</strong>
          <span>24/7 community manager</span>
        </article>
        <article className="marketing-stat">
          <strong>~$12/door</strong>
          <span>Typical PMC cost you can avoid</span>
        </article>
        <article className="marketing-stat">
          <strong>$399/mo</strong>
          <span>Professional tier starting point</span>
        </article>
        <article className="marketing-stat">
          <strong>4–6 wk</strong>
          <span>Typical launch</span>
        </article>
      </section>

      <section className="marketing-section marketing-section-alt marketing-ai-section">
        <h2>Why boards choose AI management over a management company</h2>
        <p className="marketing-section-lead">
          A full-service property management company often charges $8–$15 per lot per month — plus vendor
          markups and long contracts — for work your board can run with the right portal and an AI assistant.
          Keep your attorney and bookkeeper; replace the PMC layer for owner service and daily operations.
        </p>
        <div className="marketing-feature-grid">
          {AI_MANAGEMENT_PILLARS.map((pillar) => (
            <article key={pillar.id} className="marketing-feature-card">
              <span className="marketing-feature-icon" aria-hidden="true">{pillar.icon}</span>
              <h3>{pillar.title}</h3>
              <p>{pillar.description}</p>
            </article>
          ))}
        </div>
        <div className="marketing-mgmt-compare">
          {MANAGEMENT_COMPARISON.map((row) => (
            <article
              key={row.approach}
              className={`marketing-mgmt-card ${row.highlight ? 'marketing-mgmt-card-highlight' : ''}`}
            >
              <h3>{row.approach}</h3>
              <p className="marketing-mgmt-cost">
                {row.exampleLots} lots · <strong>${row.exampleMonthly.toLocaleString()}/mo</strong>
                <span className="marketing-mgmt-label"> ({row.monthlyLabel})</span>
              </p>
              <p>{row.notes}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="marketing-section">
        <h2>What&apos;s live in the reference site today</h2>
        <p className="marketing-section-lead">
          The {isDemoBuild ? 'demo' : 'production'} site is a working reference implementation — not a
          mockup. These modules ship as part of your deployment.
        </p>
        <div className="sales-category-grid">
          {FEATURE_CATEGORIES.map((cat) => (
            <article key={cat.id} className="sales-category-card">
              <h3>{cat.title}</h3>
              <p className="sales-category-desc">{cat.description}</p>
              <ul className="sales-feature-list">
                {cat.features.map((f) => (
                  <li key={f.id}>
                    <span className="sales-feature-name">{f.name}</span>
                    <span className="sales-feature-desc">{f.description}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="marketing-section marketing-section-alt">
        <h2>Optional modules we can build for you</h2>
        <p className="marketing-section-lead">
          Native iOS and Android apps are a separate add-on — store distribution, branded builds, and push
          notifications are substantial extra work beyond the responsive web portal. ARC and violations ship
          in beta today; smart-home amenity integrations, accounting sync, and more are scoped as custom add-ons.
        </p>
        <div className="sales-addon-grid">
          {ADDON_MODULES.map((addon) => (
            <article key={addon.id} className="sales-addon-card">
              {addon.status === 'available' && (
                <span className="sales-addon-badge sales-addon-badge-available">Add-on</span>
              )}
              {addon.status === 'roadmap' && (
                <span className="sales-addon-badge">Roadmap</span>
              )}
              {addon.status === 'beta' && (
                <span className="sales-addon-badge sales-addon-badge-beta">Beta</span>
              )}
              <h3>{addon.name}</h3>
              <p>{addon.description}</p>
              <span className="sales-addon-estimate">{addon.estimate}</span>
            </article>
          ))}
        </div>
      </section>

      <section className="marketing-section" id="smart-integrations">
        <h2>{SMART_HOME_INTEGRATIONS.headline}</h2>
        <p className="marketing-section-lead">{SMART_HOME_INTEGRATIONS.lead}</p>
        <p className="sales-integration-pricing-note">{SMART_HOME_INTEGRATIONS.pricingNote}</p>
        <div className="sales-integration-grid">
          {SMART_HOME_INTEGRATIONS.categories.map((cat) => (
            <article key={cat.id} className="sales-integration-card">
              <span className="sales-integration-icon" aria-hidden="true">{cat.icon}</span>
              <h3>{cat.title}</h3>
              <p>{cat.summary}</p>
              <ul className="sales-integration-examples">
                {cat.examples.map((ex) => (
                  <li key={ex}>{ex}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
        <div className="sales-integration-platforms">
          <p className="sales-integration-platforms-label">Platforms we commonly connect</p>
          <div className="sales-integration-platform-chips">
            {SMART_HOME_INTEGRATIONS.platforms.map((platform) => (
              <span key={platform} className="sales-integration-platform-chip">{platform}</span>
            ))}
          </div>
        </div>
        <p className="sales-integration-cta-note">
          Mention clubhouse, pool, HVAC, or other smart devices in the configurator below — we&apos;ll include
          matching add-ons in your requirements brief.
        </p>
      </section>

      <section className="marketing-section marketing-section-alt" id="pmc-checklist">
        <h2>PMC replacement checklist</h2>
        <p className="marketing-section-lead">
          Use this board-ready checklist when transitioning off a management company. Progress saves in
          your browser. Share the printable one-pager with directors before a vote.
        </p>
        <div className="sales-checklist-actions no-print">
          <Link to="/for-communities/one-pager" className="marketing-btn marketing-btn-primary">
            Open board one-pager
          </Link>
          <a href="#configurator" className="marketing-btn marketing-btn-secondary">
            Scope your portal
          </a>
        </div>
        <PmcReplacementChecklist />
      </section>

      <section className="marketing-section" id="pricing">
        <h2>Suggested pricing</h2>
        <p className="marketing-section-lead">
          Dedicated deployments on your own Firebase project — not shared multi-tenant SaaS. Prices are
          starting points; final quotes depend on lot count, integrations, and data migration.
        </p>
        <div className="sales-pricing-grid">
          {PRICING_TIERS.map((t) => (
            <article
              key={t.id}
              className={`sales-pricing-card ${t.popular ? 'sales-pricing-card-popular' : ''} ${tier.id === t.id ? 'sales-pricing-card-recommended' : ''}`}
            >
              {t.popular && <span className="sales-pricing-badge">Most popular</span>}
              {tier.id === t.id && answers.lotCount && (
                <span className="sales-pricing-badge sales-pricing-badge-match">Your match</span>
              )}
              <h3>{t.name}</h3>
              <p className="sales-pricing-tagline">{t.tagline}</p>
              <p className="sales-pricing-amount">
                <strong>${t.monthly}</strong>
                <span>/mo</span>
              </p>
              <p className="sales-pricing-setup">${t.setup.toLocaleString()} setup</p>
              <p className="sales-pricing-cap">Up to {t.lotCap} lots · +${t.perLotOver}/lot over cap</p>
              <ul className="sales-pricing-highlights">
                {t.highlights.map((h) => (
                  <li key={h}>{h}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>

        <aside className="sales-pricing-callout">
          <h3>Mobile apps are not included in base tiers</h3>
          <p>
            Every tier includes a mobile-friendly web portal. Branded iOS and Android apps — App Store /
            Google Play accounts, EAS builds, push notifications, install links, and ongoing release
            maintenance — are scoped as a separate add-on starting at{' '}
            <strong>+$5,500 setup · +$135/mo</strong>. Select &ldquo;Mobile apps for owners&rdquo; in the
            configurator to include this in your estimate.
          </p>
        </aside>

        <details className="sales-market-compare">
          <summary>How this compares to off-the-shelf HOA software (2026)</summary>
          <table className="sales-compare-table">
            <thead>
              <tr>
                <th>Vendor</th>
                <th>Model</th>
                <th>Monthly</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {MARKET_COMPARISON.map((row) => (
                <tr key={row.vendor}>
                  <td>{row.vendor}</td>
                  <td>{row.model}</td>
                  <td>{row.monthly ? `$${row.monthly}` : '—'}</td>
                  <td>{row.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="sales-compare-note">
            PMC figures are industry-typical $8–$15/door for full-service HOA management (100-lot example).
            Raklet Premium $399/mo; HOA Express Engage from $21/mo for small communities. Raklet charges
            +$299/mo for custom mobile apps — we scope native apps separately (+$135/mo from) because store
            distribution and release maintenance are significant extra work. Base tiers include the web portal;
            Nanny AI, gate access, Ring mailbox, billing, and ops monitoring ship at Professional and above.
          </p>
        </details>
      </section>

      <section className="marketing-section sales-configurator" id="configurator">
        <h2>Smart requirements configurator</h2>
        <p className="marketing-section-lead">
          Answer a few questions and we&apos;ll recommend a tier, suggest add-ons, and generate a brief you
          can email us — so we can scope your site quickly.
        </p>

        <div className="sales-config-layout">
          <div className="sales-wizard-panel">
            <div className="sales-wizard-steps">
              {wizardSections.map((sec, idx) => (
                <button
                  key={sec.title}
                  type="button"
                  className={`sales-wizard-step ${wizardStep === idx ? 'sales-wizard-step-active' : ''} ${wizardStep > idx ? 'sales-wizard-step-done' : ''}`}
                  onClick={() => setWizardStep(idx)}
                >
                  <span className="sales-wizard-step-num">{idx + 1}</span>
                  {sec.title}
                </button>
              ))}
            </div>

            <div className="sales-wizard-form">
              <h3>{currentSection.title}</h3>
              {sectionQuestions.map(renderQuestion)}
            </div>

            <div className="sales-wizard-nav">
              {wizardStep > 0 && (
                <button type="button" className="marketing-btn marketing-btn-ghost" onClick={() => setWizardStep((s) => s - 1)}>
                  Back
                </button>
              )}
              {wizardStep < wizardSections.length - 1 ? (
                <button type="button" className="marketing-btn marketing-btn-primary" onClick={() => setWizardStep((s) => s + 1)}>
                  Continue
                </button>
              ) : (
                <button type="button" className="marketing-btn marketing-btn-primary" onClick={() => setWizardStep(0)}>
                  Review from start
                </button>
              )}
            </div>
          </div>

          <aside className="sales-brief-panel">
            <h3>Your project brief</h3>

            {answers.lotCount ? (
              <>
                <div className="sales-estimate-box">
                  <p className="sales-estimate-tier">Recommended: <strong>{tier.name}</strong></p>
                  <p className="sales-estimate-amount">
                    ~<strong>${pricing.monthly}</strong>/mo
                    {pricing.lotOverage > 0 && (
                      <span className="sales-estimate-detail"> (includes ${pricing.lotOverage.toFixed(0)} lot overage)</span>
                    )}
                  </p>
                  <p className="sales-estimate-setup">~${pricing.setup.toLocaleString()} setup</p>
                </div>

                {suggestedAddons.length > 0 && (
                  <div className="sales-suggested-addons">
                    <p className="sales-suggested-label">Suggested add-ons</p>
                    {suggestedAddons.map((addon) => (
                      <label key={addon.id} className="sales-addon-toggle">
                        <input
                          type="checkbox"
                          checked={enabledAddons.has(addon.id)}
                          onChange={() => toggleAddon(addon.id)}
                        />
                        <span>{addon.name}</span>
                      </label>
                    ))}
                  </div>
                )}

                <pre className="sales-brief-preview">{brief}</pre>

                <div className="sales-brief-actions">
                  <button
                    type="button"
                    className="marketing-btn marketing-btn-primary"
                    onClick={handleSubmitBrief}
                    disabled={submitLoading}
                  >
                    {submitLoading ? 'Submitting…' : 'Submit brief'}
                  </button>
                  <button type="button" className="marketing-btn marketing-btn-secondary" onClick={handleCopyBrief}>
                    Copy brief
                  </button>
                  <a
                    href={`mailto:${salesEmail}?subject=${mailtoSubject}&body=${mailtoBody}`}
                    className="marketing-btn marketing-btn-ghost"
                  >
                    Email us instead
                  </a>
                </div>
                {submitStatus && <p className="sales-copy-status">{submitStatus}</p>}
                {copyStatus && <p className="sales-copy-status">{copyStatus}</p>}
              </>
            ) : (
              <p className="sales-brief-placeholder">
                Select your lot count and priorities to see a live estimate and requirements summary.
              </p>
            )}
          </aside>
        </div>
      </section>

      <section className="marketing-cta-band">
        <h2>Want to walk through it first?</h2>
        <p>
          Explore the full demo with sample data, then send your configurator brief to{' '}
          <a href={`mailto:${salesEmail}`}>{salesEmail}</a>.
        </p>
        {onTryDemo && (
          <button type="button" className="marketing-btn marketing-btn-primary marketing-btn-lg" onClick={onTryDemo}>
            Launch demo portal
          </button>
        )}
        {onOwnerLogin && (
          <button type="button" className="marketing-btn marketing-btn-secondary marketing-btn-lg" onClick={onOwnerLogin} style={{ marginLeft: '0.5rem' }}>
            Demo sign in
          </button>
        )}
        <p className="marketing-legal-links">
          <Link to="/">Product overview</Link>
          {' · '}
          <Link to="/privacy">Privacy Policy</Link>
          {' · '}
          <Link to="/terms">Terms and Conditions</Link>
        </p>
      </section>
    </div>
  );
}

export default HoaSalesPage;